import Vue from 'vue';
import {
  ListFactory, isValidArray, SuperDate,
} from '@razlet/utils';
import Quota from '../quota';
import BookingRule from '../booking-rule';
import Booking from '../booking';
import CimpInfo from '../cimp-info';
import Attachment from '../attachment';
import EntityFactory from '../base/factories/entity';
import Route from '../route';
import Airplane from '../airplane';
import RaidHistory from './history';
import RaidPreliminaryInforming from './pi';

const OVERLOAD_CAPACITY_CODE = ['dp_small'];

const UNDERLOAD_CAPACITY_CODE = ['dp_big'];

export default class Raid {
  data = null;

  route = null;

  load = null;

  ffm = null;

  fbl = null;

  comment = '';

  quotas = [];

  rules = [];

  bookings = [];

  history = [];

  attachments = [];

  saleClosedAt = null;
  saleOpenedAt = null;

  closedAt = null;
  openedAt = null;

  pi = null;

  ffmInfo = null;
  fblInfo = null;

  constructor (data) {
    if (data) this.updateData(data);
  }

  updateData (data) {
    this.data = data;

    const leg = {
      dictionary: data.dictionary || {},
      departure: {
        airport: data.departureAirport,
        estimatedAirport: data.estimatedDepartureAirport,
        time: data.departureDateTime,
        estimatedTime: data.estimatedDepartureDateTime,
        city: data.departureCity,
      },
      arrival: {
        airport: data.arrivalAirport,
        estimatedAirport: data.estimatedArrivalAirport,
        time: data.arrivalDateTime,
        estimatedTime: data.estimatedArrivalDateTime,
        city: data.arrivalCity,
      },
      available: data.availableCapacity,
      airplane: data.airplaneCapacity,
      airplaneCapacityCode: data.airplaneCapacityCode,
      load: data.loadFactor,
      company: data.iataCompany,
      companyCode: data.companyCode,
      number: data.number,
    };

    if (data.stopOvers) {
      leg.stopOvers = data.stopOvers.map(stopover => {
        return {
          airport: stopover.airport,
          estimatedAirport: stopover.estimatedAirport,
          departureTime: stopover.departureDateTime,
          estimatedDepartureTime: stopover.estimatedDepartureDateTime,
          arrivalTime: stopover.arrivalDateTime,
          estimatedArrivalTime: stopover.estimatedArrivalDateTime,
          city: stopover.city,
        };
      });
    }

    this.route = new Route([leg]);

    this.comment = this.data.comments;

    if (data.saleClosedAt) this.saleClosedAt = new SuperDate(data.saleClosedAt);
    if (data.saleOpenedAt) this.saleOpenedAt = new SuperDate(data.saleOpenedAt);
    if (data.closedAt) this.closedAt = new SuperDate(data.closedAt);
    if (data.openedAt) this.openedAt = new SuperDate(data.openedAt);

    if (data.pi && isValidArray(data.pi)) {
      // eslint-disable-next-line vue/max-len
      this.pi = data.pi
        .map(pi => new RaidPreliminaryInforming(pi))
        // eslint-disable-next-line vue/max-len
        .sort((a, b) => b.isDeparture === a.isDeparture ? 0 : a.isDeparture ? -1 : 1);
    }
    if (data.ffm) this.ffmInfo = new CimpInfo(data.ffm, 'FFM');
    if (data.fbl) this.fblInfo = new CimpInfo(data.fbl, 'FBL');

    // eslint-disable-next-line vue/max-len
    if (data.attachments) this.attachments = ListFactory.create(Attachment, data.attachments);
    if (data.airplane) {
      this.airplane = new Airplane({
        ...data.airplane,
        marker: this.companyMarker,
      });
    }
  }

  get id () {
    return this.data.id;
  }

  get airplaneName () {
    if (!this.airplane) return '';
    return `${this.airplane.type || ''} ${this.airplane.number || ''}`;
  }

  get company () {
    return this.data.iataCompany;
  }

  get companyMarker () {
    return this.data.companyCode;
  }

  get number () {
    return this.data.number;
  }

  get fullNumber () {
    return `${this.company} ${this.number}`;
  }

  get fullNumberNoSpace () {
    return `${this.company}${this.number}`;
  }

  get departure () {
    return this.route.path.firstLeg.departure;
  }

  get arrival () {
    return this.route.path.lastLeg.arrival;
  }

  get capacity () {
    return this.route.path.firstLeg.capacity;
  }

  get airplaneCapacityCode () {
    return this.capacity.code;
  }

  get direction () {
    return `${this.departure.airport}-${this.arrival.airport}`;
  }

  get stc () {
    return this.data.stc;
  }

  get stcName () {
    const key = `flights.stc.${this.stc}`;
    const translation = Vue.prototype.$localize.t(key);
    return translation === key ? this.stc : translation;
  }

  get flightTime () {
    const seconds = this.data.flightTime;
    const hours = Math.floor(seconds / (60 * 60));
    const minutes = Math.round(seconds / 60 - (hours * 60));

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  get flightHours () {
    const depTime = this.departure.date.nativeDate.getTime();
    const arrTime = this.arrival.date.nativeDate.getTime();
    return Math.floor((arrTime - depTime) / (1000 * 60 * 60));
  }

  get isNextDayArrival () {
    const departureDate = new Date(this.departure.date.toServer(false));
    const arrivalDate = new Date(this.arrival.date.toServer(false));
    return departureDate.getTime() < arrivalDate.getTime();
  }

  get status () {
    return this.data.status || '';
  }

  get isScheduled () {
    return this.status === 'schedule';
  }

  get isCanceled () {
    return this.status === 'canceled';
  }

  get isErrored () {
    return this.isCanceled;
  }

  get sale () {
    return this.data.sale || '-';
  }

  get isSaleClosed () {
    return this.data.saleClosed;
  }

  get saleState () {
    return this.isSaleClosed ? 'closed' : 'open';
  }

  get saleClosedAt () {
    return this.saleClosedAt;
  }

  get saleOpenedAt () {
    return this.saleOpenedAt;
  }

  get saleClosedBy () {
    return this.data.saleClosedBy;
  }

  get saleOpenedBy () {
    return this.data.saleOpenedBy;
  }

  get isClosed () {
    return this.data.closed;
  }

  get closedAt () {
    return this.closedAt;
  }

  get openedAt () {
    return this.openedAt;
  }

  get closedBy () {
    return this.data.closedBy;
  }

  get openedBy () {
    return this.data.openedBy;
  }

  get state () {
    return this.isClosed ? 'closed' : 'open';
  }

  get bookings () {
    return this.bookings
      ? this.bookings
      : [];
  }

  get isSendFFMAvailable () {
    const points = this.route.points;
    const availableDeparture = ['NNM', 'UCT'];
    return isValidArray(points)
      ? availableDeparture.includes(points[0]?.code)
      : false;
  }

  addFFM (data) {
    this.ffm = data;
  }

  addFBL (data) {
    this.fbl = data;
  }

  addLoad (data) {
    this.load = data;
  }

  addQuotas (data) {
    this.quotas = data.map(quota => EntityFactory.createRow(
      Quota,
      quota,
      { disabled: !quota.applicable }
    ));
  }

  addRules (data) {
    this.rules = data.map(rule => EntityFactory.createRow(
      BookingRule,
      rule,
      { disabled: !rule.applicable }
    ));
  }

  addBookings (data) {
    this.bookings = data.map(booking => EntityFactory.createRow(
      Booking,
      booking,
      { disabled: !booking.active }
    ));
  }

  replaceBooking (id, entityData) {
    const index = this.bookings.findIndex(item => item.id === id);
    let prevMountKey = this.bookings[index].mountKey;

    this.bookings[index] = EntityFactory.createRow(
      Booking,
      entityData,
      { mountKey: prevMountKey += 1 }
    );
  }

  addHistory (history) {
    this.history = ListFactory.create(RaidHistory, history);
  }

  fillAttachments (attachments) {
    this.attachments = ListFactory.create(Attachment, attachments);
  }

  addAttachment (data) {
    this.attachments.push(new Attachment(data));
  }

  get pageTitle () {
    // eslint-disable-next-line vue/max-len
    return `${this.fullNumberNoSpace}/${this.departure.date.shortDateUpperWithYear}/${this.direction}`;
  }

  get fullName () {
    return `${this.fullNumber}/${this.departure.date.shortDateUpper}`;
  }

  get isOverloadCapacityCode () {
    return OVERLOAD_CAPACITY_CODE.includes(this.airplaneCapacityCode);
  }

  get isUnderloadCapacityCode () {
    return UNDERLOAD_CAPACITY_CODE.includes(this.airplaneCapacityCode);
  }

  get date () {
    return this.departure?.date;
  }

  get hasPI () {
    return this.pi && isValidArray(this.pi);
  }

  get piHasTwoSides () {
    if (!this.pi) return false;
    const allSides = this.pi.map(pi => pi.side);
    return new Set([...allSides])?.size > 1;
  }

  get abilityProps () {
    return {
      company: this.companyMarker,
      departure: this.departure.code,
      arrival: this.arrival.code,
    };
  }
};
